import { useState, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { useAuth } from "./AuthProvider";
import { mapearError } from "@/lib/errores";
import { Boton } from "@/components/ui";
import { Logo } from "@/components/layout/Logo";
import { CASILLA_CONSTANCIA, CASILLA_FINANCIEROS, CASILLA_SENSIBLES, CORREO_PRIVACIDAD, DOMICILIO, PRIVACY_VERSION, RESPONSABLE } from "@/legal/aviso";

/** Un año: lo que dura la membresía de Wealth Nation. Se puede apagar antes desde Cuenta. */
export const asesorHasta = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString(); };

function Casilla({ v, set, children }: { v: boolean; set: (x: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex items-start gap-3 rounded-md border border-borde bg-white p-4">
      <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={v} onChange={(e) => set(e.target.checked)} />
      <span className="text-[15px]">{children}</span>
    </label>
  );
}

/**
 * Aviso simplificado + casillas del aviso de los abogados (sección III), ninguna pre-marcada.
 * Obligatorias para entrar: constancia del aviso y datos financieros. Opcionales: datos sensibles (si no, Mi identidad,
 * Diagnóstico y Rueda 360 piden la casilla al abrirlos) y que el asesor de RWM vea la foto (revocable desde Cuenta).
 */
export function Consentimiento() {
  const { member, privacyVersion, recargar, salir, estado } = useAuth();
  const [constancia, setConstancia] = useState(false);
  const [financieros, setFinancieros] = useState(false);
  const [sensibles, setSensibles] = useState(false);
  const [asesor, setAsesor] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const version = privacyVersion ?? PRIVACY_VERSION;

  async function aceptar() {
    setError(null); setCargando(true);
    const { error } = await supabase.rpc("accept_consent", { p_version: version, p_user_agent: navigator.userAgent.slice(0, 300), p_ip_hash: estado?.ip_hash ?? null });
    if (error) { setCargando(false); setError(mapearError(error).mensaje); return; }
    // Si falla, no bloquea: Mi identidad, Diagnóstico y Rueda 360 vuelven a pedir la casilla
    if (sensibles) await api("/api/me/sensibles", { method: "POST", json: { acepto: true, version } }).catch(() => undefined);
    if (asesor && member) await supabase.from("members").update({ share_with_facilitator_until: asesorHasta() }).eq("id", member.id);
    setCargando(false);
    await recargar();
  }

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col gap-5 p-6">
      <Logo tono="claro" />
      <h1 className="text-2xl font-semibold">Antes de empezar: aviso de privacidad</h1>
      <div className="flex flex-col gap-3 text-[15px] leading-snug text-tinta">
        <p>{RESPONSABLE}, con domicilio en {DOMICILIO}, es responsable de los datos personales recabados en Wealth Nation. Los utiliza para administrar membresías, pagos y facturación; prestar el programa, sus diagnósticos, herramientas y asesor virtual; y operar y proteger la comunidad. Los datos pueden incluir información financiera y patrimonial y, si usted decide proporcionarla, información sensible. Los consentimientos legalmente requeridos se solicitan antes de recabarla.</p>
        <p>Finalidades secundarias: enviar publicidad e invitaciones sobre otros programas y utilizar testimonios identificables solo con autorización específica. Puede negarse a las finalidades secundarias, sin afectar su membresía, escribiendo a {CORREO_PRIVACIDAD}. Los datos necesarios podrán compartirse con Revolution Wealth Management, empresa del mismo grupo, para acompañamiento patrimonial y asesoría solicitada, conforme al aviso integral.</p>
        <p>Para derechos ARCO, revocación y limitación del uso de datos: {CORREO_PRIVACIDAD}. Consulte el <a className="underline" href="/aviso-de-privacidad" target="_blank" rel="noreferrer">Aviso de Privacidad Integral</a>.</p>
      </div>
      <Casilla v={constancia} set={setConstancia}>{CASILLA_CONSTANCIA}</Casilla>
      <Casilla v={financieros} set={setFinancieros}>{CASILLA_FINANCIEROS}</Casilla>
      <Casilla v={sensibles} set={setSensibles}>{CASILLA_SENSIBLES} <span className="text-tinta-suave">Opcional. Solo hace falta para Mi identidad, el Diagnóstico y la Rueda 360; sin ella puedes usar todo lo demás.</span></Casilla>
      <Casilla v={asesor} set={setAsesor}>Acepto que mi asesor de Revolution Wealth Management vea mi foto patrimonial para acompañarme. <span className="text-tinta-suave">Opcional; se puede quitar en cualquier momento desde Cuenta.</span></Casilla>
      <p className="text-sm text-tinta-suave">Al continuar aceptas los <a className="underline" href="/terminos" target="_blank" rel="noreferrer">términos de uso</a>. Versión {version}.</p>
      {error && <p className="text-sm text-error" role="alert">{error}</p>}
      <Boton tamano="lg" disabled={!constancia || !financieros} cargando={cargando} onClick={aceptar}>Continuar</Boton>
      <Boton variante="fantasma" onClick={salir}>Salir sin aceptar</Boton>
    </main>
  );
}
